import map from 'lodash/map'
import { Dropdown, Stack } from 'react-bootstrap'
import { useConfirmDialog } from '@ztechapps/common.hook'
import { chatBatchLimit, chatBatchReplyLimit, chatStatusColor } from '@ztechapps/chat.common'
import { ShowIf, TooltipPopOver } from '@ztechapps/webui.util'
import { useConversationInboxContext } from './Context'
import { useConversationBatchActions } from './useConversationBatchActions'
import { BatchAssignModal } from './BatchAssignModal'
import { BatchTagsModal } from './BatchTagsModal'
import { BatchRemarksModal } from './BatchRemarksModal'
import { BatchReplyModal } from './BatchReplyModal'
import { chatPermissions } from '@ztechapps/chat.common'
import { useRole } from '@ztechapps/common.hook'

type BatchActionLabelProps = {
  label: string
  description: string
}

const BatchActionLabel = ({ label, description }: BatchActionLabelProps) => (
  <Stack>
    <span>{label}</span>
    <small className="text-body-secondary">{description}</small>
  </Stack>
)

export const BatchActions = () => {
  const { chatListWithSelection } = useConversationInboxContext()
  const { hasRole: canEdit } = useRole({ roleName: chatPermissions.canEdit.value })
  const { hasSelected, selectedList } = chatListWithSelection
  const selectedCount = selectedList.length
  const overLimit = selectedCount > chatBatchLimit
  const overReplyLimit = selectedCount > chatBatchReplyLimit

  const {
    closeConversations,
    cancelConversations,
    markSpamConversations,
    markReadConversations,
    closing,
    canceling,
    markingSpam,
    markingRead,
  } = useConversationBatchActions()

  const { onConfirm } = useConfirmDialog({
    title: 'Cancel conversations',
    text: `This will cancel ${selectedCount} selected conversation(s). A customer reply will not bring them back.`,
    buttons: { confirm: { text: 'Cancel conversations' } },
    confirmed: async () => {
      await cancelConversations(map(selectedList, '_id'))
    },
  })

  // The batch menu offers only writes, so a view-only holder gets no menu at all.
  if (!canEdit) return null

  return (
    <ShowIf condition={hasSelected}>
      <Dropdown>
        {/* A disabled toggle takes no pointer events, so the wrapper span carries the hover. */}
        <TooltipPopOver
          tooltipBody={overLimit ? `Select ${chatBatchLimit} conversations or fewer for a batch action.` : undefined}
        >
          <span>
            <Dropdown.Toggle variant="primary" id="conversation-batch-actions" disabled={overLimit}>
              Batch Actions ({selectedCount})
            </Dropdown.Toggle>
          </span>
        </TooltipPopOver>
        <Dropdown.Menu>
          <Dropdown.Item onClick={() => closeConversations(map(selectedList, '_id'))} disabled={closing}>
            <BatchActionLabel label="Close" description={chatStatusColor.closed.description} />
          </Dropdown.Item>
          <Dropdown.Item onClick={() => onConfirm()} disabled={canceling}>
            <BatchActionLabel label="Cancel" description={chatStatusColor.cancelled.description} />
          </Dropdown.Item>
          <Dropdown.Item onClick={() => markSpamConversations(map(selectedList, '_id'))} disabled={markingSpam}>
            <BatchActionLabel
              label="Mark as spam"
              // Distinct from Cancel: MarkChatAsSpam also records each sender on the tenant's
              // spam-sender list, so the next message from that address arrives already
              // Cancelled (libs/chat/node/src/actions/MarkChatAsSpam.ts:19-21, 76-92).
              description="Cancels each conversation and records its sender on the spam-sender list."
            />
          </Dropdown.Item>
          <Dropdown.Item onClick={() => markReadConversations(map(selectedList, '_id'))} disabled={markingRead}>
            <BatchActionLabel label="Mark as read" description="Clears the unread count on each conversation." />
          </Dropdown.Item>
          <BatchReplyModal
            disabled={overReplyLimit}
            title={
              overReplyLimit ? `Select ${chatBatchReplyLimit} conversations or fewer for a batch reply.` : undefined
            }
          />
          <Dropdown.Divider />
          <BatchAssignModal />
          <BatchTagsModal />
          <BatchRemarksModal />
        </Dropdown.Menu>
      </Dropdown>
    </ShowIf>
  )
}
